import { z } from 'zod'

import {
  ACCENTS,
  DEFAULT_APPEARANCE,
  DEFAULT_SETTINGS,
  type Appearance,
  type Settings,
} from './types'

/**
 * The two jsonb columns, checked on the way in.
 *
 * `settings` and `appearance` are opaque blobs in Postgres, written by the
 * app as arbitrary records and read back with a cast: `{...DEFAULT_SETTINGS,
 * ...(ws.settings as Partial<Settings>)}`. A cast asserts, it does not check.
 * Anything that ever put a wrong value in one of those columns -- an older
 * build, a hand-edited row, a future migration -- would flow straight into
 * typed code that trusts it, and `weekStart` is read by the date arithmetic
 * every report depends on.
 *
 * Unknown keys are dropped and bad values fall back to the default rather
 * than throwing: one unrecognised preference should cost that preference, not
 * the whole workspace.
 */

const settingsSchema = z
  .object({
    weekStart: z.enum(['Monday', 'Sunday']),
    timeFormat: z.enum(['12h', '24h']),
    durFormat: z.enum(['h:mm', 'decimal']),
    requireProject: z.boolean(),
    requireClient: z.boolean(),
    autoStop: z.boolean(),
    roundToMinute: z.boolean(),
    allowManual: z.boolean(),
  })
  .partial()

const appearanceSchema = z
  .object({
    navLayout: z.enum(['sidebar', 'topnav']),
    timerStyle: z.enum(['bar', 'card']),
    reportsChart: z.enum(['bars', 'line']),
    accent: z.enum(ACCENTS),
  })
  .partial()

/** Keeps every field that is valid; silently drops the ones that are not. */
function keepValid<T extends object>(schema: z.ZodType<Partial<T>>, raw: unknown): Partial<T> {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {}

  const whole = schema.safeParse(raw)
  if (whole.success) return whole.data

  // One bad key should not discard the good ones beside it.
  const kept: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (schema.safeParse({ [key]: value }).success) kept[key] = value
  }
  const partial = schema.safeParse(kept)
  return partial.success ? partial.data : {}
}

export function parseSettings(raw: unknown): Settings {
  const kept = keepValid<Settings>(settingsSchema, raw)

  /*
   * roundTo15 became roundToMinute. The stored jsonb still holds the old key
   * for any workspace that set it, and keepValid drops keys the schema does
   * not know -- so without this the toggle would silently switch itself off
   * for anyone who had turned it on. Only honoured when the new key is absent,
   * so an explicit new value always wins.
   */
  if (kept.roundToMinute === undefined && raw !== null && typeof raw === 'object') {
    const legacy = (raw as Record<string, unknown>).roundTo15
    if (typeof legacy === 'boolean') kept.roundToMinute = legacy
  }

  /*
   * 'h:mm:ss' became 'h:mm' when seconds left the interface. The stored value
   * is the old string for anyone who chose it, and the enum no longer accepts
   * it -- so without this they would silently be switched to decimal hours.
   */
  if (kept.durFormat === undefined && raw !== null && typeof raw === 'object') {
    if ((raw as Record<string, unknown>).durFormat === 'h:mm:ss') kept.durFormat = 'h:mm'
  }

  return { ...DEFAULT_SETTINGS, ...kept }
}

export function parseAppearance(raw: unknown): Appearance {
  return { ...DEFAULT_APPEARANCE, ...keepValid<Appearance>(appearanceSchema, raw) }
}
